/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import dummyImage from '../assets/images/dummyprofile.jpg';
import {
  GridContainer,
  BoxContainer,
  SuccessModal,
  ContainedButton,
  CreateEditRequestModal,
  ConsentModal,
  HREditModal,
  ViewEditRequestModal,
} from '../components/index';
import { useEffect, useState } from 'react';
import { Box, Grid, Typography, Tabs, Tab } from '@mui/material';
import { Edit, Visibility } from '@mui/icons-material';

import { useOutletContext } from 'react-router-dom';
import { apiUrl } from '../utilities/apiUrl';
import { DepartmentBox } from '../components/Profile/DepartmentBox';
import { ProjectBox } from '../components/Profile/ProjectBox';
import { ProfileButtons } from '../components/Profile/ProfileButtons';
import { UserForms } from '../components/Profile/UserForms';
import { UserInfo } from '../components/Profile/UserInfo';
import { EmergencyContact } from '../components/Profile/EmergencyContact';
import { AccountManagement } from '../components/Profile/AccountManagement';
import { EmployeeDetails } from '../components/Profile/EmployeeDetails';

const nameStyle = {
  fontWeight: '700',
  padding: '1rem 1.5rem',
  mt: 2,
  py: 1,
  textAlign: 'start',
};

export const UserProfile = () => {
  const [empId] = useOutletContext();
  const [editMode, setEditMode] = useState(false);
  const [emp, setEmp] = useState({});
  const [fullName, setFullName] = useState('');
  const [address, setAddress] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [tel, setTel] = useState('');
  const [username, setUsername] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isValidPassword, setIsValidPassword] = useState(true);
  const [isNewEmpty, setIsNewEmpty] = useState(true);
  const [isConfirmEmpty, setIsConfirmEmpty] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [deptId, setDeptId] = useState('');
  const [departmentData, setDepartmentData] = useState([]);
  const [deptMembers, setDeptMembers] = useState([]);
  const [currentTab, setTab] = useState('personalInfo');
  const [isEditRequestModalOpen, setIsEditRequestModalOpen] = useState(false);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [isViewEditRequestModalOpen, setIsViewEditRequestModalOpen] =
    useState(false);
  const [isHREditModalOpen, setIsHREditModalOpen] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(null);
  const [pendingEditRequest, setPendingEditRequest] = useState({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchPendingRequests = async (empId) => {
    try {
      const res = await fetch(
        apiUrl(`/eis/empeditrequest?status=PENDING&empid=${empId}`),
        { method: 'GET', credentials: 'include' }
      );
      const editData = await res.json();

      if (editData.success && editData.data.length !== 0) {
        setHasPendingRequest(true);
      } else {
        setHasPendingRequest(false);
      }

      setPendingEditRequest(editData.data[0]);
    } catch (error) {
      console.error('Error fetching employee edit requests:', error);
    }
  };

  // RETRIEVE USER INFO
  useEffect(() => {
    fetch(apiUrl('/employee/' + empId), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setEmp(data.data);
        setFullName(`${data.data.firstname} ${data.data.lastname}`);
        setDeptId(data.data.deptid);

        return fetch(apiUrl('/department/' + data.data.deptid), {
          method: 'GET',
          credentials: 'include',
        });
      })
      .then((response) => response.json())
      .then((data) => {
        setDepartmentData(data.data);

        return fetch(apiUrl('/employee'), {
          method: 'GET',
          credentials: 'include',
        });
      })
      .then((response) => response.json())
      .then((data) => {
        setDeptMembers(data.data.filter((member) => member.deptid === deptId));

        // Fetch pending edit requests of user if there is one
        fetchPendingRequests(empId);
      });
  }, [empId, editMode, deptId, refreshTrigger]);

  const handleSave = () => {
    setEditMode(false);

    const noPassData = {
      address1: address,
      state: state,
      country: country,
      email: email,
      cellphone: phone,
      username: username,
      homephone: tel,
    };

    const withPassData = {
      address1: address,
      state: state,
      country: country,
      email: email,
      cellphone: phone,
      homephone: tel,
      username: username,
      // As of v1.0.0-beta.3, change password is separated from edit user profile
      password: confirmPassword,
    };

    fetch(apiUrl('/employee/' + emp._id), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        isNewEmpty && isConfirmEmpty ? noPassData : withPassData
      ),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setModalOpen(true);
        } else {
          alert(data.message);
        }
      });
  };

  const handleEdit = () => {
    setEditMode(true);

    setAddress(emp.address1);
    setState(emp.state);
    setCountry(emp.country);
    setEmail(emp.email);
    setPhone(emp.cellphone);
    setTel(emp.homephone);
    setUsername(emp.username);
  };

  // As of v1.0.0-beta.3, change password is separated from edit user profile
  const handleNewPassChange = (e) => {
    setNewPassword(e.target.value);
    e.target.value === '' ? setIsNewEmpty(true) : setIsNewEmpty(false);
  };

  // As of v1.0.0-beta.3, change password is separated from edit user profile
  const handleConfirmPassChange = (e) => {
    setConfirmPassword(e.target.value);
    e.target.value !== newPassword
      ? setIsValidPassword(false)
      : setIsValidPassword(true);

    e.target.value === '' ? setIsConfirmEmpty(true) : setIsConfirmEmpty(false);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setNewPassword('');
    // As of v1.0.0-beta.3, change password is separated from edit user profile
    setConfirmPassword('');
    setIsValidPassword(true);
  };

  const handleEditRequestModalOpen = () => {
    setIsEditRequestModalOpen(true);
  };

  const handleChangeTab = (event, newTabValue) => {
    setTab(newTabValue);
  };

  return (
    <GridContainer
      key={refreshTrigger}
      selectedPage={3}
      sx={{ minHeight: '100%' }}
    >
      <Grid
        item
        md={6}
        sx={{ padding: '12px', display: 'flex', flexDirection: 'column' }}
      >
        <BoxContainer
          sx={{ flex: 1 }}
          bg={{ bgcolor: 'rgb(26, 32, 44, 0.85)' }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
            }}
          >
            <img
              src={dummyImage}
              alt={fullName}
              style={{ width: '100%', objectFit: 'cover', height: '250px' }}
            />
            <Typography sx={nameStyle} variant="h4">
              {fullName.toUpperCase()}
            </Typography>
            {!editMode ? (
              <UserInfo empData={emp} />
            ) : (
              <UserForms
                username={username}
                address={address}
                state={state}
                country={country}
                email={email}
                phone={phone}
                tel={tel}
                onChangeAddress={(e) => setAddress(e.target.value)}
                onChangeState={(e) => setState(e.target.value)}
                onChangeCountry={(e) => setCountry(e.target.value)}
                onChangeEmail={(e) => setEmail(e.target.value)}
                onChangePhone={(e) => setPhone(e.target.value)}
                onChangeTel={(e) => setTel(e.target.value)}
                onChangeUsername={(e) => setUsername(e.target.value)}
                // As of v1.0.0-beta.3, change password is separated from edit user profile
                onChangeNewPass={handleNewPassChange}
                onChangeConfirmPass={handleConfirmPassChange}
                confirmPassError={!isValidPassword}
              />
            )}
            {/* <ProfileButtons
              editMode={!editMode}
              handleEdit={handleEdit}
              handleCancel={() => {
                setEditMode(false);
                setNewPassword("");
                setConfirmPassword("");
                setIsValidPassword(true);
              }}
              handleSave={handleSave}
              disabledSave={!isValidPassword || !username || !newPassword || !confirmPassword}
            /> */}
          </Box>
        </BoxContainer>
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
        sx={{ padding: '12px', display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              width: 1,
              height: 1,
              flexDirection: 'column',
              bgcolor: 'rgb(26, 32, 44, 0.85)',
              boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
              borderRadius: '5px',
            }}
          >
            <Typography sx={nameStyle} variant="h4">
              Emergency Contact
            </Typography>

            <EmergencyContact empId={empId} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              width: 1,
              height: 1,
              flexDirection: 'column',
              bgcolor: 'rgb(26, 32, 44, 0.85)',
              boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
              borderRadius: '5px',
            }}
          >
            <Typography sx={nameStyle} variant="h4">
              Account Management
            </Typography>

            <AccountManagement />
          </Box>
        </Box>
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ padding: '12px', display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              width: 1,
              height: 1,
              flexDirection: 'column',
              bgcolor: 'rgb(26, 32, 44, 0.85)',
              boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
              borderRadius: '5px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pr: 2,
              }}
            >
              <Typography sx={nameStyle} variant="h4">
                Employee Details
              </Typography>

              {emp && hasPendingRequest !== null && (
                <ContainedButton
                  style={{
                    mt: 'auto',
                    width: 'auto',
                    maxWidth: 300,
                    padding: 2,
                    py: 1,
                    '& .MuiButton-startIcon': {
                      marginRight: 1,
                    },
                  }}
                  onClick={() => {
                    if (emp.role === 'SUPER_ADMIN') {
                      // SUPER_ADMIN can only edit profile
                      setIsConsentModalOpen(true);
                      return;
                    }

                    if (!hasPendingRequest) {
                      // Create Edit Request
                      setIsConsentModalOpen(true);
                      return;
                    }

                    // User has a pending request open view modal
                    // Fetch again for the edit request before showing the modal to ensure that the data is updated
                    fetchPendingRequests(empId);
                    setIsViewEditRequestModalOpen(true);
                  }}
                  btnTitle={
                    emp.role === 'SUPER_ADMIN'
                      ? 'Edit Profile'
                      : !hasPendingRequest
                        ? 'Create Edit Request'
                        : 'View Edit Request'
                  }
                  startIcon={
                    emp.role === 'SUPER_ADMIN' ? (
                      <Edit />
                    ) : !hasPendingRequest ? (
                      <Edit />
                    ) : (
                      <Visibility />
                    )
                  }
                />
              )}
            </Box>

            <Tabs
              value={currentTab}
              onChange={handleChangeTab}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="scrollable auto tabs "
              sx={{ p: '12px' }}
            >
              <Tab value="personalInfo" label="Personal Information" />
              <Tab value="addresses" label="Addresses" />
              <Tab
                value="familyBackground"
                label="Family Background & Relations"
              />
              <Tab value="educBackground" label="Educational Background" />
              <Tab value="civService" label="Civil Service Eligibility" />

              <Tab value="workExp" label="Work Experience" />
              <Tab value="volWork" label="Voluntary Work" />
              <Tab value="trainingProgs" label="Training Programs" />
              <Tab value="history" label="History" />
            </Tabs>

            <EmployeeDetails empData={emp} currentTab={currentTab} />
          </Box>
        </Box>
      </Grid>

      {/* <Grid
        item
        md={6}
        sx={{
          padding: "12px",
          display: "flex",
          height: "100%",
          flexDirection: "column",
        }}
      > */}
      {/* <Grid item sx={{ mb: 3 }}>
          <ProjectBox />
        </Grid> */}
      {/* Projects section is temporarily hidden as of 1.0.0-beta.3 */}
      {/* <Grid item>
          <DepartmentBox 
          members={deptMembers} 
          deptName={departmentData.deptname}
          />
        </Grid> */}
      {/* </Grid> */}

      <ConsentModal
        openNextModal={() => {
          if (emp.role === 'SUPER_ADMIN') {
            setIsHREditModalOpen(true);
          } else {
            setIsEditRequestModalOpen(true);
          }
        }}
        open={isConsentModalOpen}
        secondaryClick={() => {
          setIsConsentModalOpen(false);
        }}
        onClose={() => {
          setIsConsentModalOpen(false);
        }}
      />

      <CreateEditRequestModal
        frontendsetter={() => {
          setHasPendingRequest(true);
        }}
        empid={empId}
        empData={emp}
        open={isEditRequestModalOpen}
        onClose={() => {
          setIsEditRequestModalOpen(false);
        }}
      />

      <ViewEditRequestModal
        open={isViewEditRequestModalOpen}
        editRequest={pendingEditRequest}
        onClose={() => setIsViewEditRequestModalOpen(false)}
        onSuccess={() => setRefreshTrigger((c) => c + 1)}
      />

      <HREditModal
        empid={empId}
        lastname={emp.lastname}
        firstname={emp.firstname}
        middlename={emp.middlename}
        email={emp.email}
        open={isHREditModalOpen}
        onClose={() => {
          setIsHREditModalOpen(false);
        }}
        onSuccess={() => setRefreshTrigger((c) => c + 1)}
      />

      <SuccessModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        close={handleModalClose}
        desc={'You have successfully updated your information.'}
      />
    </GridContainer>
  );
};
